// src/pages/calendar/BookingLinksPage.tsx
// Let outsiders book time with me: list booking links, create/edit them,
// copy the public URL, preview availability, delete.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useCalendar, useCalendarBooking, useI18n } from '@react/hooks';
import { Button, Badge, Alert } from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { BookingLink } from '@core/types/calendar';
import type { PaginatedRequest } from '@core/types/pagination';
import { BookingLinkDialog } from '@/features/calendar/BookingLinkDialog';
import { durationToMinutes, errMessage, fmtDate, fmtTime, publicBookingUrl } from '@/features/calendar/time';

export const BookingLinksPage = observer(() => {
  const { t, locale } = useI18n();
  const store = useCalendarBooking();
  const calendarStore = useCalendar();

  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<BookingLink | null>(null);
  const [deleting, setDeleting] = useState<BookingLink | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [previewFor, setPreviewFor] = useState<string | null>(null);
  const [previewSlots, setPreviewSlots] = useState<{ start?: string; end?: string }[] | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = () => {
    setError(null);
    store
      .ListBookingLinks({
        pagination: { cursor: '', limit: 100, direction: 'forward' },
      } as PaginatedRequest)
      .catch((e: unknown) => setError(errMessage(e, t('calendar.bookingLinks.loadError'))));
  };

  useEffect(() => {
    load();
    calendarStore.ListCalendars({
      pagination: { cursor: '', limit: 100, direction: 'forward' },
    } as PaginatedRequest).catch(() => {});
  }, []);

  const copyUrl = async (link: BookingLink) => {
    try {
      await navigator.clipboard.writeText(publicBookingUrl(link.slug));
      setCopiedId(link.entity?.id ?? null);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // clipboard unavailable — the URL is still visible in the row
    }
  };

  const preview = async (link: BookingLink) => {
    if (previewFor === link.entity?.id) {
      setPreviewFor(null);
      return;
    }
    setPreviewFor(link.entity?.id ?? null);
    setPreviewLoading(true);
    setPreviewSlots(null);
    try {
      const res = await store.GetBookingLinkAvailability(link.slug);
      setPreviewSlots((res.slots ?? []).map((s) => ({ start: s.start_time, end: s.end_time })));
    } catch {
      setPreviewSlots([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">{t('calendar.bookingLinks.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('calendar.bookingLinks.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/calendar" className="text-sm text-primary hover:underline">
            {t('calendar.backToCalendar')}
          </Link>
          <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }}>
            {t('calendar.bookingLinks.newLink')}
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="error">
          {error}
          <Button size="sm" variant="secondary" className="ml-2" onClick={load}>
            {t('calendar.retry')}
          </Button>
        </Alert>
      )}

      {store.isLoading && store.items.length === 0 && (
        <div className="space-y-2" aria-busy="true">
          <div className="h-16 animate-pulse rounded-md bg-muted" />
          <div className="h-16 animate-pulse rounded-md bg-muted" />
        </div>
      )}

      {!store.isLoading && store.items.length === 0 && !error && (
        <div className="rounded-md border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">{t('calendar.bookingLinks.empty')}</p>
          <Button size="sm" className="mt-3" onClick={() => { setEditing(null); setDialogOpen(true); }}>
            {t('calendar.bookingLinks.newLink')}
          </Button>
        </div>
      )}

      <div className="space-y-2">
        {store.items.map((link) => {
          const id = link.entity?.id ?? '';
          return (
            <div key={id} className="rounded-md border border-border p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="truncate text-base font-medium text-foreground">{link.title}</h2>
                    {link.status === 'BOOKING_LINK_STATUS_PAUSED' && (
                      <Badge variant="warning">{t('calendar.bookingLinks.paused')}</Badge>
                    )}
                    {link.status === 'BOOKING_LINK_STATUS_ARCHIVED' && (
                      <Badge variant="default">{t('calendar.bookingLinks.archived')}</Badge>
                    )}
                  </div>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    {t('calendar.bookingLinks.durationLabel', { minutes: durationToMinutes(link.duration) || 0 })}
                  </p>
                  <p className="mt-1 truncate font-mono text-xs text-muted-foreground">{publicBookingUrl(link.slug)}</p>
                </div>
                <div className="flex shrink-0 flex-wrap gap-1.5">
                  <Button size="sm" variant="secondary" onClick={() => copyUrl(link)}>
                    {copiedId === id ? t('calendar.bookingLinks.copied') : t('calendar.bookingLinks.copyUrl')}
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => preview(link)} aria-expanded={previewFor === id}>
                    {t('calendar.bookingLinks.preview')}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={async () => {
                      // Load the full link — the list payload may be trimmed.
                      let full = link;
                      try {
                        full = await store.GetBookingLink(id);
                      } catch {
                        full = link;
                      }
                      setEditing(full);
                      setDialogOpen(true);
                    }}
                  >
                    {t('common.edit')}
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => setDeleting(link)}>
                    {t('common.delete')}
                  </Button>
                </div>
              </div>
              {previewFor === id && (
                <div className="mt-3 border-t border-border pt-2">
                  {previewLoading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
                  {!previewLoading && previewSlots && previewSlots.length === 0 && (
                    <p className="text-sm text-muted-foreground">{t('calendar.bookingLinks.noSlots')}</p>
                  )}
                  {!previewLoading && previewSlots && previewSlots.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {previewSlots.slice(0, 12).map((s, i) => (
                        <span key={i} className="rounded-md border border-border px-2 py-0.5 text-xs text-foreground">
                          {fmtDate(s.start, locale)} {fmtTime(s.start, locale)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <BookingLinkDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        link={editing}
        calendars={calendarStore.items}
        onSaved={load}
      />

      <ConfirmDialog
        open={!!deleting}
        title={t('calendar.bookingLinks.deleteTitle')}
        description={t('calendar.bookingLinks.deleteDescription')}
        destructive
        isConfirming={store.isDeleting}
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting?.entity?.id) return;
          try {
            await store.DeleteBookingLink(deleting.entity.id);
            setDeleting(null);
            load();
          } catch (e) {
            setError(errMessage(e, t('calendar.bookingLinks.deleteError')));
            setDeleting(null);
          }
        }}
      />
    </div>
  );
});
